import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button, Card, Form, Input, Modal, Popconfirm, Space, Table } from 'antd';
import { PlusOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { NameCell } from '../../components/NameCell';
import type { ListItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { errorMessage } from '../../api/client';
import { useCreateList, useDeleteList, useLists, useUpdateList } from '../../features/audience/api';
import { EmptyState, tableLocale } from '../../components/states';
import { fmtDateTime } from '../../utils/format';
import { ProjectFormItem, useProjectColumn } from '../../components/ProjectSelect';
import { useProjectScope } from '../../features/projects/scope';
import { MentionTextArea } from '../../components/Mentions';

type ListForm = { name: string; description?: string | null; projectId?: string | null };

export function ListsPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const lists = useLists();
  const create = useCreateList();
  const update = useUpdateList();
  const remove = useDeleteList();
  const [editing, setEditing] = useState<ListItem | 'new' | null>(null);
  const [form] = Form.useForm<ListForm>();
  const scope = useProjectScope();
  const projectColumn = useProjectColumn<ListItem>();
  const openNew = () => {
    form.resetFields();
    form.setFieldsValue({ projectId: scope.current?.id ?? null });
    setEditing('new');
  };

  const save = async (v: ListForm) => {
    try {
      if (editing === 'new') {
        const l = await create.mutateAsync(v);
        setEditing(null);
        navigate(`/lists/${l.id}`);
        return;
      }
      if (editing) await update.mutateAsync({ id: editing.id, ...v });
      message.success('List saved');
      setEditing(null);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Lists"
        subtitle="Hand-picked sets of contacts. Add people from the contacts table or from a list page."
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>
            New list
          </Button>
        }
      />
      <Card>
      <Table<ListItem>
        rowKey="id"
        loading={lists.isLoading}
        locale={tableLocale(lists, <EmptyState description="No lists yet. Lists are hand-picked groups, for example people to invite to an event." action={<Button type="primary" icon={<PlusOutlined />} onClick={openNew}>New list</Button>} />)}
        dataSource={lists.data ?? []}
        pagination={false}
        columns={[
          { title: 'List', dataIndex: 'name', render: (v, l) => <NameCell name={v} sub={l.description ?? 'No description'} to={`/lists/${l.id}`} icon={<UnorderedListOutlined />} /> },
          ...projectColumn,
          { title: 'Members', dataIndex: 'memberCount', width: 120, render: (n: number) => n.toLocaleString() },
          { title: 'Updated', dataIndex: 'updatedAt', width: 160, render: (v) => fmtDateTime(v) },
          {
            key: 'actions',
            align: 'right',
            render: (_, l) => (
              <Space>
                <Button size="small" onClick={() => { form.setFieldsValue({ name: l.name, description: l.description, projectId: l.projectId }); setEditing(l); }}>Edit</Button>
                <Popconfirm title={`Delete list "${l.name}"? Contacts are kept.`} okText="Delete" okButtonProps={{ danger: true, loading: remove.isPending }} onConfirm={() => remove.mutateAsync(l.id).then(() => message.success('List deleted')).catch((e) => message.error(errorMessage(e)))}>
                  <Button size="small" danger>Delete</Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      </Card>
      <Modal title={editing === 'new' ? 'New list' : 'Edit list'} open={!!editing} onCancel={() => setEditing(null)} onOk={() => form.submit()} okText={editing === 'new' ? 'Create' : 'Save'} confirmLoading={create.isPending || update.isPending} destroyOnHidden>
        <Form form={form} layout="vertical" onFinish={save} requiredMark={false}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Give the list a name' }]}>
            <Input autoFocus maxLength={120} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <MentionTextArea autoSize={{ minRows: 2 }} maxLength={1000} />
          </Form.Item>
          <ProjectFormItem />
        </Form>
      </Modal>
    </div>
  );
}
